import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Typography,
  IconButton,
  Card,
  CardContent,
  Stack,
  Avatar,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowBack, ArrowBackIos, Edit, RemoveRedEyeOutlined, Inventory, Category, Add, Info, FilterList, Clear } from '@mui/icons-material';
import { fetchCategorias, fetchProductos } from '../../../utils/generalsFetch';
import Selector from '../../../components/Selector';
import { capitalizeFirstLetter } from '../../../utils/generalFunctions';
import { useNavigate } from 'react-router-dom';
import AccessDeniedBox from '../../../components/AccessDeniedBox';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontWeight:"bold"
  },
}));

const ProductsTable = () => { 
  const [categorias, setCategorias] = useState([])
  const [products, setProducts] = useState([])
  const navigate = useNavigate(); 


  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('');
  const [filtros, setFiltros] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [campos, setCampos] = useState([]);
  const [accessDenied, setAccessDenied] = React.useState(false);


  useEffect(() => {
    fetchCategorias({ idcompany: localStorage.getItem('idcompany'), usuarioId: localStorage.getItem('id') }).then(data => {
      setCategorias(data);
    }).catch((error) => {
      setAccessDenied(true);
    })
  }, []);




  const filteredRows = useMemo(() => {
    if (!products || !Array.isArray(products) || products.length === 0) return [];
    
    return products.filter((producto) => {
      return campos.every((campo) => {
        const filtro = filtros[campo.id] || '';
        if (!filtro) return true; // No filter applied for this field
        
        let fieldValue = '';
        
        // Check if the field exists directly on the producto object
        if (producto.hasOwnProperty(campo.id) && campo.id !== 'id') {
          fieldValue = String(producto[campo.id] || '');
        } else {
          // Try to get the value from the campos JSON
          try {
            const camposData = JSON.parse(producto.campos || '{}');
            fieldValue = String(camposData[campo.id] || '');
          } catch (error) {
            fieldValue = '';
          }
        }
        
        // Handle different field types based on campo.type
        const fieldType = campo.type || 'texto';
        
        if (fieldType === 'opciones') {
          // For select fields, exact match (case-insensitive)
          return fieldValue.toLowerCase() === filtro.toLowerCase();
        } else if (fieldType === 'numero' || fieldType === 'monto') {
          // For numbers, try to match as numbers
          const filterNum = parseFloat(filtro);
          const fieldNum = parseFloat(fieldValue);
          if (!isNaN(filterNum) && !isNaN(fieldNum)) {
            return fieldNum === filterNum;
          }
          // Fallback to string comparison
          return fieldValue.toLowerCase().includes(filtro.toLowerCase());
        } else if (fieldType === 'fecha') {
          // For dates, try to match as dates
          try {
            const filterDate = new Date(filtro);
            const fieldDate = new Date(fieldValue);
            if (!isNaN(filterDate.getTime()) && !isNaN(fieldDate.getTime())) {
              // Compare dates (ignore time)
              return filterDate.toDateString() === fieldDate.toDateString();
            }
          } catch (error) {
            // Fallback to string comparison
          }
          return fieldValue.toLowerCase().includes(filtro.toLowerCase());
        } else if (fieldType === 'booleano') {
          // For boolean fields, exact match
          return fieldValue.toLowerCase() === filtro.toLowerCase();
        } else {
          // For text fields (texto, ID, numerotel), case-insensitive partial match
          return fieldValue.toLowerCase().includes(filtro.toLowerCase());
        }
      });
    });
  }, [products, filtros, campos]);


  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Category Selector - Only show when category is selected */}
      {categoriaSeleccionada && !accessDenied ? (
      <Box display="flex" flexDirection="row" width="100%" alignItems="center" justifyContent="space-between" mt={-4}>
      <FormControl sx={{ mb: 2, minWidth: 200 }}>
        <Selector
          value={categoriaSeleccionada}
          onChange={(e) => {
            setCategoriaSeleccionada(e.target.value);
            fetchProductos({ idcompany: localStorage.getItem('idcompany'), usuarioId: localStorage.getItem('id'), idcategoria: e.target.value }).then(data => {
              setProducts(Array.isArray(data) ? data : [])
            }).catch((error) => {
              setAccessDenied(true);
              setProducts([]);
            });        
            setCampos(JSON.parse(categorias.find(cat => cat.value === e.target.value).fields));
            setFiltros({});
            setPage(0);
          }}
          options={categorias}
          placeholder="Categoría"
        />
      </FormControl>
      </Box>
      ) : null}

      {/* Empty State when no category is selected */}
      {!categoriaSeleccionada && !accessDenied ? (
        <Card sx={{ 
          borderRadius: 3, 
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          border: '1px solid #e0e0e0',
          mt: 2
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Stack spacing={3} alignItems="center">
              {/* Icon */}
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  mb: 2
                }}
              >
                <Category sx={{ fontSize: 40 }} />
              </Avatar>

              {/* Title */}
              <Typography variant="h4" fontWeight={700} color="text.primary" sx={{ mb: 1 }}>
                Selecciona una Categoría de Productos
              </Typography>

              {/* Subtitle */}
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, lineHeight: 1.6 }}>
                Para ver y gestionar los productos, primero debes seleccionar una categoría de productos desde el selector superior.
              </Typography>

              {/* Info Box */}
              <Box
                sx={{
                  backgroundColor: '#f0f7ff',
                  border: '1px solid #b3d9ff',
                  borderRadius: 2,
                  p: 3,
                  maxWidth: 500,
                  mt: 2
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Info sx={{ color: '#1976d2', fontSize: 24 }} />
                  <Box>
                    <Typography variant="body1" fontWeight={600} color="primary.main" sx={{ mb: 0.5 }}>
                      ¿Cómo funciona?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Cada categoría tiene sus propios campos personalizados y productos específicos. 
                      Selecciona una categoría para comenzar a gestionar tus productos.
                    </Typography>
                  </Box>
                </Stack>
              </Box>

            
              {/* Category Selector */}
              <Box sx={{ mt: 3, minWidth: 300, maxWidth: 400 }}>
                <Typography variant="body1" fontWeight={600} color="text.primary" sx={{ mb: 2 }}>
                  Selecciona una categoría para comenzar:
                </Typography>
                <FormControl fullWidth>
                  <Selector
                    value={categoriaSeleccionada}
                    onChange={(e) => {
                      setCategoriaSeleccionada(e.target.value);
                      fetchProductos({ idcompany: localStorage.getItem('idcompany'), usuarioId: localStorage.getItem('id'), idcategoria: e.target.value }).then(data => {
                        
                        setProducts(Array.isArray(data["productos"]) ? data["productos"] : [])
                      }).catch((error) => {
                        setAccessDenied(true);
                        setProducts([]);
                      });        
                      setCampos(JSON.parse(categorias.find(cat => cat.value === e.target.value).fields));
                      setFiltros({});
                      setPage(0);
                    }}
                    options={categorias}
                    placeholder="Seleccionar categoría de productos"
                    sx={{
                      backgroundColor: 'white',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: '1.1rem',
                        fontWeight: 500
                      }
                    }}
                  />
                </FormControl>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {/* Filters and Table - Only show when category is selected */}
      {categoriaSeleccionada && !accessDenied ? (
        <>
          {/* Dynamic Filters */}
          {campos.length > 0 && (
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 2, 
              mb: 3, 
              p: 2,
              backgroundColor: '#f8f9fa',
              borderRadius: 2,
              border: '1px solid #e0e0e0'
            }}>
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FilterList sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="h6" fontWeight={600} color="text.primary">
                    Filtros de Búsqueda
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {filteredRows.length} de {products.length} productos
                </Typography>
              </Box>
              {campos.map((campo) => {
                const fieldLabel = `Filtrar por ${campo.name === "id" ? capitalizeFirstLetter(campo.alias) : capitalizeFirstLetter(campo.name)}`;
                const fieldType = campo.type || 'texto';
                
                // For opciones fields, get unique options from products
                const getSelectorOptions = () => {
                  if (fieldType === 'opciones') {
                    const options = new Set();
                    products.forEach(producto => {
                      let value = '';
                      if (producto.hasOwnProperty(campo.id) && campo.id !== 'id') {
                        value = String(producto[campo.id] || '');
                      } else {
                        try {
                          const camposData = JSON.parse(producto.campos || '{}');
                          value = String(camposData[campo.id] || '');
                        } catch (error) {
                          value = '';
                        }
                      }
                      if (value && value.trim() !== '') {
                        options.add(value);
                      }
                    });
                    return Array.from(options).sort();
                  }
                  return [];
                };

                const selectorOptions = getSelectorOptions();

                // Render different input types based on campo.type
                if (fieldType === 'opciones') {
                  return (
                    <Box key={campo.id} sx={{ minWidth: 200 }}>   
                      <Typography variant="body2" fontWeight={600} color="text.secondary" textAlign="left"> 
                        {fieldLabel}
                      </Typography>
                      <FormControl
                        size="small"
                        fullWidth
                        sx={{
                          backgroundColor: 'white',
                          borderRadius: 1
                        }}
                      >
                        <Select
                          value={filtros[campo.id] || ''}
                          onChange={(e) =>
                            setFiltros((prev) => ({
                              ...prev,
                              [campo.id]: e.target.value,
                            }))
                          }
                          displayEmpty
                          sx={{
                            backgroundColor: 'white',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderRadius: 1
                            }
                          }}
                        >
                          <MenuItem value="">
                            <em>Todos los {campo.name === "id" ? capitalizeFirstLetter(campo.alias) : capitalizeFirstLetter(campo.name)}</em>
                          </MenuItem>
                          {selectorOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  );
                }

                // For boolean fields, use a select with Yes/No options
                if (fieldType === 'booleano') {
                  return (
                    <Box key={campo.id} sx={{ minWidth: 200 }}>
                      <Typography variant="body2" fontWeight={600} color="text.secondary" textAlign="left">
                        {fieldLabel}
                      </Typography>
                      <FormControl
                        size="small"
                        fullWidth
                        sx={{
                          backgroundColor: 'white',
                          borderRadius: 1
                        }}
                      >
                        <Select
                          value={filtros[campo.id] || ''}
                          onChange={(e) =>
                            setFiltros((prev) => ({
                              ...prev,
                              [campo.id]: e.target.value,
                            }))
                          }
                          displayEmpty
                          sx={{
                            backgroundColor: 'white',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderRadius: 1
                            }
                          }}
                        >
                          <MenuItem value="">
                            <em>Todos</em>
                          </MenuItem>
                          <MenuItem value="true">Sí</MenuItem>
                          <MenuItem value="false">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  );
                }

                // Default to TextField for other types
                return (
                  <Box key={campo.id} sx={{ minWidth: 200 }}>
                    <Typography variant="body2" fontWeight={600} color="text.secondary" textAlign="left">
                      {fieldLabel}
                    </Typography>
          <TextField
            variant="outlined"
            size="small"
                      fullWidth
                      type={
                        fieldType === 'numero' || fieldType === 'monto' ? 'number' : 
                        fieldType === 'fecha' ? 'date' :
                        'text'
                      }
            value={filtros[campo.id] || ''}
            onChange={(e) =>
              setFiltros((prev) => ({
                ...prev,
                [campo.id]: e.target.value,
              }))
            }
                      sx={{
                        backgroundColor: 'white',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1
                        }
                      }}
                      placeholder={
                        fieldType === 'numero' || fieldType === 'monto' ? 'Ingresa un número...' :
                        fieldType === 'fecha' ? 'Selecciona una fecha...' :
                        fieldType === 'ID' ? 'Ingresa ID...' :
                        fieldType === 'numerotel' ? 'Ingresa teléfono...' :
                        'Buscar...'
                      }
                    />
                  </Box>
                );
              })}
              {/* Clear Filters Button */}
              {Object.keys(filtros).some(key => filtros[key]) && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Clear />}
                  onClick={() => setFiltros({})}
                  sx={{
                    height: 40,
                    alignSelf: 'flex-end',
                    textTransform: 'none',
                    fontWeight: 500,
                    color: 'error.main',
                    borderColor: 'error.main',
                    '&:hover': {
                      backgroundColor: 'error.light',
                      borderColor: 'error.main',
                      color: 'white'
                    }
                  }}
                >
                  Limpiar Filtros
                </Button>
              )}
      </Box>
          )}

      <TableContainer component={Paper} sx={{ borderRadius: 2, minHeight: 400, }}>
      
        <Table>
          <TableHead>
            <TableRow>
              {campos.map((campo) => (
                <StyledTableCell key={campo} sx={{ fontWeight: 'bold', fontSize:15, height:20}}>
                  {capitalizeFirstLetter(campo.alias!=''?campo.alias:campo.name)}
                </StyledTableCell>
              ))}
              <StyledTableCell sx={{ fontWeight: 'bold', fontSize:15, height:20}}></StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((producto) => (
              
              <TableRow key={producto.id}>

                {campos.map((campo) => {
                  // Check if the key exists in producto for this campo
                  if (producto.hasOwnProperty(campo.id) && campo.id!='id') {
                    return (
                      <TableCell key={campo.id} sx={{fontWeight:500}}>
                        {producto[campo.id]}
                      </TableCell>
                    );
                  }
                  return(
                    <TableCell key={campo.id} sx={{fontWeight:500}}>{JSON.parse(producto.campos)[campo.id]?JSON.parse(producto.campos)[campo.id]:'-'}</TableCell>                  
                  )
                })}
                <TableCell>
                  <IconButton variant="contained" size="small" sx={{ color: "#617689" }} onClick={()=>{navigate(`/logistica/productos/${producto.id}`)}}>
                    <RemoveRedEyeOutlined />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedRows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={campos.length}
                  align="center"
                  sx={{
                    height: 200, // Adjust height as needed
                    verticalAlign: 'middle',
                    fontSize: 18,
                    fontWeight: 'bold',
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    width="100%"
                    sx={{ minHeight: 200 }}
                  >
                    <Typography variant="h6" color="text.secondary">
                      No hay resultados
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </TableContainer>
        </>
      ) : null}
      {accessDenied ? (
        <Box>
          <br></br>
          <br></br>
          <AccessDeniedBox
            title={'Acceso denegado'}
            message={'No tienes permisos para ver esta sección de Productos. Contacta a tu administrador para solicitar acceso o verifica tus roles y permisos.'}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default ProductsTable;

